import React, { useState, useEffect } from 'react';
import { 
  FaCalendarAlt, 
  FaChartLine, 
  FaMoneyBillWave, 
  FaShoppingCart, 
  // FaUsers, // Неиспользуемый импорт
  FaSpinner
} from 'react-icons/fa';
import './SalesStatistics.css';

const SalesStatistics = () => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для фильтров по датам
  const [dateRange, setDateRange] = useState({
    startDate: getOneMonthAgo(),
    endDate: new Date().toISOString().split('T')[0]
  });
  
  // Состояние для отфильтрованных данных
  const [filteredData, setFilteredData] = useState([]);
  
  // Функция для получения даты месяц назад
  function getOneMonthAgo() {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  }
  
  // Загрузка данных статистики
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const telegramId = localStorage.getItem('telegram_id');
        
        if (!telegramId) {
          throw new Error('Ошибка авторизации: отсутствует Telegram ID');
        }
        
        const response = await fetch('/api/payments/stats', {
          headers: {
            'X-Telegram-ID': telegramId
          }
        });
        
        if (!response.ok) {
          throw new Error(`Ошибка сервера: ${response.status}`);
        }
        
        const data = await response.json();
        setStatsData(data);
        
        // Применяем фильтры по умолчанию
        filterData(data, dateRange);
      } catch (err) {
        console.error('Ошибка при загрузке статистики:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [dateRange]); // Добавлена отсутствующая зависимость dateRange
  
  // Функция для фильтрации данных по датам
  const filterData = (data, dateFilter) => {
    if (!data || !data.paymentsByMonth) return;
    
    const startDate = new Date(dateFilter.startDate);
    const endDate = new Date(dateFilter.endDate);
    endDate.setHours(23, 59, 59, 999); // Устанавливаем конец дня
    
    // Фильтруем данные по месяцам, которые попадают в выбранный диапазон
    const filtered = data.paymentsByMonth.filter(payment => {
      const paymentDate = new Date(payment.year, payment.month - 1, 15); // 15-ое число месяца
      return paymentDate >= startDate && paymentDate <= endDate;
    });
    
    setFilteredData(filtered);
  };
  
  // Обработчик изменения дат
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const newDateRange = { ...dateRange, [name]: value };
    setDateRange(newDateRange);
    
    if (statsData) {
      filterData(statsData, newDateRange);
    }
  };
  
  // Форматирование числа в денежный формат
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  // Расчет общей суммы отфильтрованных данных
  const calculateFilteredTotal = () => {
    if (!filteredData.length) return 0;
    return filteredData.reduce((sum, item) => sum + item.amount, 0);
  };
  
  // Расчет общего количества отфильтрованных продаж
  const calculateFilteredCount = () => {
    if (!filteredData.length) return 0;
    return filteredData.reduce((sum, item) => sum + item.count, 0);
  };
  
  return (
    <div className="sales-statistics">
      {statsData && (
        <div className="stats-container">
          <div className="stat-card full-width">
            <h3>Общая выручка</h3>
            <div className="stat-value">{formatCurrency(statsData.totalAmount)}</div>
          </div>
          
          <div className="stats-row">
            <div className="stat-card">
              <h3>Всего продаж</h3>
              <div className="stat-value">{statsData.totalPayments}</div>
            </div>
            <div className="stat-card">
              <h3>Уникальных клиентов</h3>
              <div className="stat-value">{statsData.uniqueBuyers}</div>
            </div>
          </div>
        </div>
      )}

      <div className="stats-header">
        <h2><FaChartLine /> Статистика продаж</h2>
        <div className="date-filters">
          <div className="date-filter">
            <label><FaCalendarAlt /> От:</label>
            <input 
              type="date" 
              name="startDate" 
              value={dateRange.startDate}
              onChange={handleDateChange}
              max={dateRange.endDate}
            />
          </div>
          <div className="date-filter">
            <label><FaCalendarAlt /> До:</label>
            <input 
              type="date" 
              name="endDate" 
              value={dateRange.endDate}
              onChange={handleDateChange}
              min={dateRange.startDate}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <FaSpinner className="spinner" />
          <p>Загрузка статистики...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>Ошибка при загрузке данных: {error}</p>
        </div>
      ) : statsData ? (
        <div className="stats-cards">
          <div className="stats-card filtered">
            <div className="stats-card-icon"><FaMoneyBillWave /></div>
            <div className="stats-card-content">
              <h3>Выручка за период</h3>
              <p className="stats-value">{formatCurrency(calculateFilteredTotal())}</p>
            </div>
          </div>
          
          <div className="stats-card filtered">
            <div className="stats-card-icon"><FaShoppingCart /></div>
            <div className="stats-card-content">
              <h3>Продаж за период</h3>
              <p className="stats-value">{calculateFilteredCount()}</p>
            </div>
          </div>
        </div>
      ) : (
        <p>Нет данных о продажах</p>
      )}
    </div>
  );
};

export default SalesStatistics;
